import gsap from 'gsap';
import { reducedMotion } from './shared/motion';
import { glyph } from './shared/icons';
import './startup.scss';

import { soundEnabled, setSoundEnabled, soundVolume } from './shared/audio';

export function setupStartup({ onEnter, onSuspend, onResume }) {
  const screen = document.querySelector('#startup-screen');
  const sound = document.querySelector('#startup-sound');
  const skip = document.querySelector('#startup-skip');
  const status = document.querySelector('#startup-status');
  const ignition = screen.querySelector('.crt-ignition');
  const beam = screen.querySelector('.crt-beam');
  const chime = new Audio('/concepts/aero/startup-chime.wav');
  chime.id = 'startup-audio';
  screen.append(chime);
  chime.preload = 'auto';
  chime.volume = 0.14*soundVolume();
  document.addEventListener('portfolio-volume-change',()=>chime.volume=.14*soundVolume());
  let soundOn = soundEnabled();
  let phase = 'idle';
  let timeline;
  let finishTimer;
  let returnFocus;
  let audioBlocked=false;
  let hintTimer;
  let audioAttempt=0;
  const audioHint=document.createElement('button');
  audioHint.className='startup-audio-hint';audioHint.hidden=true;
  audioHint.textContent='Replay startup with sound';
  audioHint.title='Your browser blocked automatic sound. Replay once to hear it.';
  document.body.append(audioHint);
  audioHint.addEventListener('click',()=>{audioHint.hidden=true;open();});
  function playChime(){
    if(!soundOn||phase!=='starting')return;
    const attempt=++audioAttempt;
    chime.play().then(()=>{if(attempt!==audioAttempt)return;audioBlocked=false;audioHint.hidden=true;}).catch(error=>{if(attempt!==audioAttempt)return;audioBlocked=error.name==='NotAllowedError';if(phase==='done')audioHint.hidden=!audioBlocked||!soundOn;});
  }
  screen.addEventListener('pointerdown',event=>{if(!event.target.closest('button')&&audioBlocked)playChime();});
  screen.addEventListener('keydown',event=>{if(event.key!=='Escape'&&audioBlocked)playChime();});

  const stopSound = () => { audioAttempt++; chime.pause(); chime.currentTime = 0; };
  function updateSound() {
    sound.setAttribute('aria-pressed', String(soundOn));
    sound.innerHTML = `${glyph('sound')}<span>Sound ${soundOn ? 'on' : 'off'}</span>`;
    sound.classList.toggle('is-muted', !soundOn);
  }
  function finish({ silent = false } = {}) {
    if (!screen.open) return;
    clearTimeout(finishTimer);
    timeline?.kill();
    if (silent) stopSound();
    phase = 'done';
    screen.close();
    audioHint.hidden=silent||!audioBlocked||!soundOn;
    clearTimeout(hintTimer);hintTimer=setTimeout(()=>audioHint.hidden=true,8000);
    onEnter();
    onResume();
    if (returnFocus?.isConnected && !returnFocus.closest('[hidden]')) returnFocus.focus({ preventScroll: true });

  }
  function open() {
    if (screen.open) return;
    returnFocus = document.activeElement;
    onSuspend();
    stopSound();
    phase = 'ready';
    screen.classList.remove('is-starting');
    status.textContent = '';
    gsap.set([screen, ignition, beam], { clearProps: 'all' });
    screen.showModal();
    document.documentElement.classList.remove('startup-pending');
    screen.focus({ preventScroll: true });
    start();
  }
  function start() {
    if (phase !== 'ready') return;
    phase = 'starting';
    screen.classList.add('is-starting');
    status.textContent = 'Starting desktop';
    audioBlocked=false;audioHint.hidden=true;
    playChime();
    // A safety deadline also covers background throttling or interrupted animation.
    finishTimer = setTimeout(() => finish(), 2500);
    if (reducedMotion.matches) {
      timeline = gsap.timeline({ onComplete: () => finish() });
      timeline.to(screen, { opacity: 0, duration: 0.15 });
      return;
    }
    timeline = gsap.timeline({ defaults: { ease: 'power3.out' }, onComplete: () => finish() });
    timeline.set(ignition, { opacity: 1 }, 0)
      .set(beam, { scaleX: 0.002, scaleY: 0.003, opacity: 0 }, 0)
      .to(beam, { opacity: 1, scaleX: 0.85, duration: 0.32, ease: 'power2.in' }, 0.16)
      .to(beam, { scaleX: 1, scaleY: 1, duration: 0.46, ease: 'expo.out' }, 0.48)
      // The illuminated phosphor settles directly onto the real desktop.
      .to(screen, { opacity: 0, duration: 0.9, ease: 'power2.inOut' }, 0.86);
  }
  skip.addEventListener('click', () => finish({ silent: true }));
  screen.addEventListener('cancel', event => { event.preventDefault(); finish({ silent: true }); });
  sound.addEventListener('click', () => {
    setSoundEnabled(!soundEnabled());
  });
  document.querySelector('[data-startup-replay]').addEventListener('click', open);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) return;
    stopSound();
    if (phase === 'starting') finish({ silent: true });
  });
  window.addEventListener('pagehide', stopSound);
  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches && phase === 'starting') finish();
  });
  document.addEventListener('portfolio-sound-change',()=>{soundOn=soundEnabled();if(!soundOn){stopSound();audioHint.hidden=true;}else if(phase==='starting')playChime();updateSound();});
  updateSound();
  onEnter();
  open();
}
